import React from "react";
import { Box, Card, CardContent, Chip, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import {
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { has, isArray } from "lodash";
import moment from "moment";
import { useDate } from "../../hooks/useDate";
import { useTimezone } from "../../hooks/useTimezone";
import { i18n } from "../../translate/i18n";
import { useStyles } from "./CompaniesManagerImprovedStyles";

// D8.6.2 — Grid/tabla de empresas (presentacional; recibe records + onSelect).

export default function CompaniesManagerGridImproved(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  const { dateToClient } = useDate();
  const { formatDateTime } = useTimezone();

  const renderStatus = (row) => {
    return row.status === false ? i18n.t("companies.values.no") : i18n.t("companies.values.yes");
  };

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderRecurrence = (recurrence) => {
    if (!recurrence) return "-";
    
    const recurrenceMap = {
      "MENSAL": i18n.t("companies.values.monthly"),
      "BIMESTRAL": i18n.t("companies.values.bimonthly"),
      "TRIMESTRAL": i18n.t("companies.values.quarterly"),
      "SEMESTRAL": i18n.t("companies.values.semiannual"),
      "ANUAL": i18n.t("companies.values.annual"),
    };
    
    return recurrenceMap[recurrence] || recurrence;
  };

  const renderCampaignsStatus = (row) => {
    if (has(row, "settings") && isArray(row.settings) && row.settings.length > 0) {
      const setting = row.settings.find((s) => s.key === "campaignsEnabled");
      if (setting) {
        return setting.value === "true" ? i18n.t("companies.values.enabled") : i18n.t("companies.values.disabled");
      }
    }
    return i18n.t("companies.values.disabled");
  };

  const getRowClass = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return classes.warningRow;
      }
      if (diff <= 0) {
        return classes.dangerRow;
      }
    }
    return "";
  };

  const getStatusChipClass = (status) => {
    return status ? classes.statusActive : classes.statusInactive;
  };

  const getCampaignsChipClass = (campaigns) => {
    return campaigns === i18n.t("companies.values.enabled") ? classes.campaignsEnabled : classes.campaignsDisabled;
  };

  if (records.length === 0) {
    return (
      <Card className={classes.tableCard}>
        <CardContent className={classes.emptyState}>
          <BusinessIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
          <Typography variant="h6" color="textSecondary">
            {i18n.t("companies.grid.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {i18n.t("companies.grid.emptySubtitle")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.tableCard}>
      <Table>
        <TableHead className={classes.tableHeader}>
          <TableRow>
            <TableCell align="center" style={{ width: "60px" }}>
              #
            </TableCell>
            <TableCell align="left">
              <BusinessIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.name")}
            </TableCell>
            <TableCell align="left">
              <EmailIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.email")}
            </TableCell>
            <TableCell align="left">
              <PhoneIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.phone")}
            </TableCell>
            <TableCell align="left">
              <StarIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.plan")}
            </TableCell>
            <TableCell align="left">
              <CheckCircleIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.campaigns")}
            </TableCell>
            <TableCell align="left">
              <CheckCircleIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.status")}
            </TableCell>
            <TableCell align="left">
              <CalendarIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.createdAt")}
            </TableCell>
            <TableCell align="left">
              <CalendarIcon style={{ marginRight: 8 }} />
              {i18n.t("companies.table.dueDate")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((row, key) => (
            <TableRow 
              className={`${classes.tableRow} ${getRowClass(row)}`} 
              key={key}
              onClick={() => onSelect(row)}
            >
              <TableCell align="center" className={classes.tableCell}>
                <Tooltip title={i18n.t("companies.tooltips.editCompany")}>
                  <IconButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(row);
                    }} 
                    className={classes.editButton}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Typography variant="body2" style={{ fontWeight: "600" }}>
                  {row.name || "-"}
                </Typography>
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                {row.email || "-"}
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                {row.phone || "-"}
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Chip 
                  label={renderPlan(row)} 
                  className={classes.planChip}
                  size="small"
                />
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Chip 
                  label={renderCampaignsStatus(row)} 
                  className={`${classes.statusChip} ${getCampaignsChipClass(renderCampaignsStatus(row))}`}
                  size="small"
                />
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Chip 
                  label={renderStatus(row)} 
                  className={`${classes.statusChip} ${getStatusChipClass(row.status)}`}
                  size="small"
                />
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                {formatDateTime(row.createdAt)}
              </TableCell>
              <TableCell align="left" className={classes.tableCell}>
                <Box>
                  <Typography variant="body2">
                    {dateToClient(row.dueDate)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {renderRecurrence(row.recurrence)}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
